import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Star, MapPin, Clock, Award, CheckCircle, Calendar, MessageSquare, Dumbbell, Languages, Briefcase, Shield, ChevronLeft, Users2, Image, Users } from 'lucide-react';
import ConnectionButton from '../components/matchmaking/ConnectionButton';
import ProfileStats from '../components/members/profile/ProfileStats';
import CitiesPlayedModal from '../components/members/CitiesPlayedModal';
import DashboardLayout from '../components/DashboardLayout';
import { Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { fetchActivityImage } from '@/lib/fetchActivityImage';
import { supabase } from '@/lib/supabase';
import { useConnectionStore } from '../store/connectionStore';
import { TabScroller } from '../components/ui/tabs/TabScroller';
import TrainerMediaGallery from '../components/trainers/TrainerMediaGallery';
import TrainerBookingModal from '../components/trainers/TrainerBookingModal';
import { useAuth } from '@/contexts/AuthContext';
import { fetchCityImageFromMultipleSources } from '@/lib/fetchCityImage';
import PostsTab from '../components/profile/tabs/PostsTab';
import NetworkTab from '../components/members/tabs/NetworkTab';
import { normalizeCity } from '@/lib/city/normalizer';
import SocialLinks from '../components/social/SocialLinks';
import MemberMatchesModal from '../components/members/modals/MemberMatchesModal';

interface TrainerProfileProps {}

const TrainerProfile: React.FC<TrainerProfileProps> = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { connections } = useConnectionStore();
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [trainer, setTrainer] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sportBackgrounds, setSportBackgrounds] = useState<Record<string, string>>({});
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [sessions, setSessions] = useState<any[]>([]);
  const [reviews, setReviews] = useState<any[]>([]);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [sportBackground, setSportBackground] = useState<string | null>(null);
  const [showMatchesModal, setShowMatchesModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'about' | 'reviews' | 'sessions' | 'moments' | 'connections'>('about');
  const [showTrainerBookingModal, setShowTrainerBookingModal] = useState(false);
  const [sessionToBook, setSessionToBook] = useState<any | null>(null);
  const [showCitiesModal, setShowCitiesModal] = useState(false);
  const [trainerCitiesList, setTrainerCitiesList] = useState<string[]>([]);
  const [trainerProfile, setTrainerProfile] = useState<any>(null);
  const [expandedSessions, setExpandedSessions] = useState<Record<string, boolean>>({});
  
  // Check if we should open the booking modal automatically
  useEffect(() => {
    if (location.state?.bookNow && trainer) {
      setShowTrainerBookingModal(true);
    }
  }, [location.state, trainer]);

  useEffect(() => {
    const fetchTrainer = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch trainer data from Supabase
        const { data: userData, error: userError } = await supabase
  .from('users')
  .select(`
    id,
    auth_id,
    full_name,
    avatar_url,
    city,
    gender,
    linkedin_url,
    facebook_url,
    instagram_url,
    user_sports (
      sports (
        name,
        emoji
      )
    )
  `)
  .eq('auth_id', id)
  .eq('role', 'trainer')
  .maybeSingle();


        if (userError) throw userError;
        if (!userData) throw new Error('Trainer not found');


        // Fetch Matches Played and Cities Played
        const today = new Date().toISOString().split('T')[0];
        const { count: playedCount, error: countError } = await supabase
          .from('activities')
          .select('*', { head: true, count: 'exact' })
          .or(`player_1_id.eq.${userData.id},player_2_id.eq.${userData.id}`)
          .eq('status', 'confirmed')
          .lt('date', today);

        if (countError) {
          console.warn('Error fetching matches count for trainer:', countError);
        }
        const trainerMatchesPlayed = playedCount || 0;

        const { data: cityData, error: cityError } = await supabase
          .from('activities')
          .select('venue_city')
          .or(`player_1_id.eq.${userData.id},player_2_id.eq.${userData.id}`)
          .eq('status', 'confirmed')
          .lt('date', today);

        if (cityError) {
          console.warn('Error fetching cities for trainer:', cityError);
        }
        const uniqueCities = new Set(
          cityData?.map(act => act.venue_city?.split(',')[0].trim()).filter(Boolean)
        );
        const trainerCitiesList = Array.from(uniqueCities);
        setTrainerCitiesList(trainerCitiesList);

        // Fetch Connections Count
        const { count: connectionsCount, error: connectionsError } = await supabase
          .from('connections')
          .select('*', { head: true, count: 'exact' })
          .or(`initiator_id.eq.${userData.id},receiver_id.eq.${userData.id}`)
          .eq('status', 'accepted');

        if (connectionsError) {
          console.warn('Error fetching connections count for trainer:', connectionsError);
        }
        const trainerConnectionsCount = connectionsCount || 0;

        // Fetch trainer ratings
       const { data: ratingsData, error: ratingsError } = await supabase
  .from('user_ratings')
  .select(
    'overall_rating, total_ratings, avg_punctuality, avg_communication, avg_sportsmanship, avg_skill_match'
  )
  .eq('user_id', userData.id)
  .maybeSingle();


    // Fetch trainer profile
const { data: profile, error: profileError } = await supabase
  .from('trainer_profiles')
  .select('headline, bio, hourly_rate, languages')
  .eq('user_id', userData.id)
  .maybeSingle();


if (profileError) throw profileError;
const trainerProfile = profile ?? {};

/* Fetch latest reviews for this trainer */
const { data: reviewRows, error: reviewErr } = await supabase
  .from('user_reviews')
  .select(
    `
      id,
      feedback,
      rating_overall,
      timestamp,
      author:author_id (
        full_name,
        avatar_url
      )
    `
  )
  .eq('target_id', userData.id)
  .order('timestamp', { ascending: false });

if (reviewErr) throw reviewErr;
setReviews(reviewRows ?? []);

        
        /* Fetch session types for this trainer */
const { data: sessionRows, error: sessionErr } = await supabase
  .from('trainer_session_types')
  .select('title, description, price_cents, duration_min, price_mode, group_min, group_max')
  .eq('user_id', userData.id)
  .order('price_cents');

if (sessionErr) throw sessionErr;
setSessions(sessionRows ?? []);



        
        // Format sports from user_sports
        const formattedSports = userData.user_sports?.map(us => 
          `${us.sports.emoji} ${us.sports.name}`
        ) || ['🏋️‍♂️ Fitness'];

        // Build trainer object from fetched data
       const trainerData = {
  db_id: userData.id,
  id:    userData.auth_id,
  name:  userData.full_name,
  matchesPlayed: trainerMatchesPlayed,
  citiesPlayed: trainerCitiesList,
  connections: trainerConnectionsCount,
  image: userData.avatar_url || 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=800&q=80',

  /* ─── ratings ─── */
  sports: formattedSports,
  rating: ratingsData?.overall_rating
    ? parseFloat(ratingsData.overall_rating).toFixed(1)
    : '4.5',
  reviews: ratingsData?.total_ratings || 0,
  avgPunctuality:   ratingsData?.avg_punctuality   ?? 0,
  avgCommunication: ratingsData?.avg_communication ?? 0,
  avgSportsmanship: ratingsData?.avg_sportsmanship ?? 0,
  avgSkillMatch:    ratingsData?.avg_skill_match   ?? 0,

  /* ─── everything else you already had ─── */
  city:        userData.city        ?? '',
  yearsExperience: 3,
  availability: ['Weekdays', 'Evenings', 'Weekends'],
  priceRange:  trainerProfile.hourly_rate ? `$${trainerProfile.hourly_rate}` : '—',
  specialties: formattedSports.map(s => s.split(' ').slice(1).join(' ')),
  certifications: ['Certified Trainer'],
  achievements: ['Professional Trainer'],
  languages:
    Array.isArray(trainerProfile.languages) && trainerProfile.languages.length
      ? trainerProfile.languages
      : ['English'],
  gender: userData.gender || 'rather_not_say',
  bio:         trainerProfile.bio      ?? '',
  headline:    trainerProfile.headline ?? '',
  company:     userData.company        ?? '',
  linkedinUrl: userData.linkedin_url   ?? null,
  facebookUrl: userData.facebook_url   ?? null,
  instagramUrl: userData.instagram_url ?? null,
};

        setTrainer(trainerData);
        setTrainerProfile(trainerProfile);
      } catch (err) {
        console.error('Error fetching trainer:', err);
        setError('Failed to load trainer profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrainer();
  }, [id]);

  // Fetch sport background image for the first sport
  useEffect(() => {
    if (!trainer?.sports || trainer.sports.length === 0) return;
    
    const loadSportImage = async () => {
      try {
        // Extract the sport name without emoji
        const sportName = trainer.sports[0].split(' ').slice(1).join(' ');
        const imageUrl = await fetchActivityImage(sportName);
        setSportBackground(imageUrl);
      } catch (error) {
        console.error('Error fetching sport background image:', error);
        setSportBackground('https://images.unsplash.com/photo-1486218119243-13883505764c?auto=format&fit=crop&w=1600&q=80');
      }
    };
    
    loadSportImage();
  }, [trainer?.sports]);

  // Fetch background images for each sport
  useEffect(() => {
    const loadBackgroundImages = async () => {
      if (!trainer) return;
      
      const backgrounds: Record<string, string> = {};
      
      // Get unique sports from trainer
      const uniqueSports = trainer.sports.map((sport: string) => {
        const sportName = sport.split(' ').slice(1).join(' ');
        return sportName;
      });
      
      // Fetch images for each sport
      for (const sport of uniqueSports) {
        try {
          const imageUrl = await fetchActivityImage(sport);
          backgrounds[sport] = imageUrl;
        } catch (error) {
          console.error(`Failed to fetch image for ${sport}:`, error);
          // Use a default image if fetch fails
          backgrounds[sport] = 'https://images.unsplash.com/photo-1486218119243-13883505764c?auto=format&fit=crop&w=1600&q=80';
        }
      }
      
      setSportBackgrounds(backgrounds);
    };
    
    loadBackgroundImages();
  }, [trainer]);

  const handleBookNow = () => {
    setSessionToBook(null);
    setShowTrainerBookingModal(true);
  };

  const handleSendMessage = async () => {
    try {
      if (!user) {
        navigate('/');
        return;
      }

      // Get user's database ID
      const { data: userData, error: userDataError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();

      if (userDataError) throw userDataError;
      if (!userData) throw new Error('User not found');

      // Get trainer's database ID
      const { data: trainerData, error: trainerError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', id)
        .single();

      if (trainerError) throw trainerError;
      if (!trainerData) throw new Error('Trainer not found');

      // Create or get conversation
      const { data: conversation, error: conversationError } = await supabase.rpc(
        'get_or_create_conversation',
        {
          user1_id: userData.id,
          user2_id: trainerData.id
        }
      );

      if (conversationError) throw conversationError;

      // Navigate to messages with this conversation active
      navigate('/dashboard/messages', {
        state: {
          activeChat: conversation,
          initialMessage: `Hi, I'm interested in booking a training session.`,
          showMobileList: false
        }
      });
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
  };

  // Check if the trainer is blocked
  const isBlocked = React.useMemo(() => {
    return connections.some(conn => 
      conn.other_user?.auth_id === trainer?.id && conn.status === 'blocked'
    );
  }, [connections, trainer?.id]);

  const handleBack = () => {
    navigate('/dashboard/training');
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col justify-center items-center min-h-[60vh] gap-4">
          <Loader2 className="h-12 w-12 text-gold-400 animate-spin" />
          <p className="text-dark-800">Loading trainer profile...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !trainer) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-white mb-4">Trainer Not Found</h2>
          <p className="text-dark-800 mb-6">
            {error || "We couldn't find the trainer you're looking for."}
          </p>
          <Button
            onClick={() => navigate('/dashboard/training')}
            className="bg-gold-400 hover:bg-gold-500 text-dark-50"
          >
            Back to Trainers
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center text-dark-800 hover:text-white transition-colors"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          <span>Back to Trainers</span>
        </button>

        {/* Profile Header - Similar to Member Profile */}
<div className="bg-white dark:bg-dark-200 rounded-2xl overflow-hidden border border-dark-300">
{/* City Background */}
<div className="relative h-48 overflow-hidden rounded-2xl">
  <img
    src={sportBackground || 'https://images.unsplash.com/photo-1486218119243-13883505764c?auto=format&fit=crop&w=1600&q=80'}
    alt={`${trainer.sports[0]?.split(' ').slice(1).join(' ') || 'Fitness'} background`}
    className="w-full h-full object-cover object-center"
  />
  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white dark:to-dark-200" />
</div>


          {/* Profile Content */}
          <div className="relative px-6 pb-8">
            <div className="flex flex-col items-center -mt-24">
              {/* Profile Image with Trainer Badge */}
              <div className="relative">
                <img
                  src={trainer.image}
                  alt={trainer.name}
                  className="w-48 h-48 rounded-full object-cover border-4 border-dark-200 shadow-xl ring-4 ring-dark-200"
                />
                <div className="absolute bottom-2 right-2 bg-gold-400 rounded-full p-2.5 border-2 border-dark-200">
                  <Dumbbell className="h-7 w-7 text-dark-50" />
                </div>
              </div>
              
              {/* Name and Basic Info */}
              <h1 className="text-3xl font-bold text-white mt-6 mb-2">{trainer.name}</h1>
              <div className="text-center">
                <div className="flex flex-wrap justify-center gap-1.5 mt-2 mb-3">
                  {trainer.sports.map((sport: string, index: number) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1.5 bg-dark-300 rounded-full text-sm font-medium text-white hover:bg-dark-400 transition-colors"
                    >
                      {sport}
                    </span>
                  ))}
                </div>
                
                {/* Location */}
                <div className="flex items-center justify-center text-dark-800 mt-2">
                  <MapPin className="h-5 w-5 mr-1.5 text-gold-400" />
                  <span className="text-lg">{trainer.city}</span>
                </div>
                
                {/* Languages */}
                <div className="flex items-center justify-center text-dark-800 mt-2">
                  <Languages className="h-5 w-5 mr-1.5 text-gold-400" />
                  <span className="text-lg">
                    {trainer.languages.join(', ')}
                  </span>
                </div>
                
                {/* Social Links Placeholder */}
                <SocialLinks
                  linkedinUrl={trainer.linkedinUrl}
                  facebookUrl={trainer.facebookUrl}
                  instagramUrl={trainer.instagramUrl}
                  className="mt-3"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Profile Stats */}
        <ProfileStats
          member={{
            ...trainer,
            matchesPlayed: trainer.matchesPlayed,
            citiesPlayed: trainer.citiesPlayed,
            connections: trainer.connections,
            rating: parseFloat(trainer.rating)
          }}
          onShowConnections={() => setActiveTab('connections')}
          onShowCities={(cities) => {
            setTrainerCitiesList(cities);
            setShowCitiesModal(true);
          }}
          onShowMatches={() => setShowMatchesModal(true)}
          onShowReviews={() => setActiveTab('reviews')}
        />

      {/* Action Buttons */}
<div className="flex flex-wrap items-center gap-2">
  <ConnectionButton
    memberId={trainer.id}
    memberName={trainer.name}
    memberImage={trainer.image}
    className="px-4 py-2 text-sm font-medium"
  />
  <Button 
    onClick={handleBookNow}
    className="bg-gold-400 hover:bg-gold-500 text-dark-50 px-4 py-2 text-sm font-medium"
  >
    <Calendar className="h-4 w-4 mr-2" />
    Book Session
  </Button>
  <Button
    onClick={handleSendMessage}
    className="bg-dark-300 hover:bg-dark-400 text-white px-4 py-2 text-sm font-medium"
  >
    <MessageSquare className="h-4 w-4 mr-2" />
    Message
  </Button>
</div>


        {/* Tabs Navigation - Similar to Member Profile */}
        <TabScroller className="overflow-x-auto no-scrollbar whitespace-nowrap -mx-4 px-4 border-b border-dark-300 touch-pan-x">
          <div className="flex items-center space-x-4 px-4">
            <button
              onClick={() => setActiveTab('about')}
              className={cn(
                "px-4 py-2 -mb-px border-b-2 transition-colors whitespace-nowrap flex-shrink-0",
                activeTab === 'about'
                  ? "border-gold-400 text-gold-400"
                  : "border-transparent text-dark-800 hover:text-white"
              )}
            >
              About
            </button>
            <button
              onClick={() => setActiveTab('moments')}
              className={cn(
                "px-4 py-2 -mb-px border-b-2 transition-colors whitespace-nowrap flex-shrink-0",
                activeTab === 'moments'
                  ? "border-gold-400 text-gold-400"
                  : "border-transparent text-dark-800 hover:text-white"
              )}
            >
              <div className="flex items-center">
                <Image className="h-4 w-4 mr-1.5" />
                <span>Moments</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('connections')}
              className={cn(
                "px-4 py-2 -mb-px border-b-2 transition-colors whitespace-nowrap flex-shrink-0",
                activeTab === 'connections'
                  ? "border-gold-400 text-gold-400"
                  : "border-transparent text-dark-800 hover:text-white"
              )}
            >
              <div className="flex items-center">
                <Users className="h-4 w-4 mr-1.5" />
                <span>Connections</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={cn(
                "px-4 py-2 -mb-px border-b-2 transition-colors whitespace-nowrap flex-shrink-0",
                activeTab === 'reviews'
                  ? "border-gold-400 text-gold-400"
                  : "border-transparent text-dark-800 hover:text-white"
              )}
            >
              <div className="flex items-center">
                <Star className="h-4 w-4 mr-1.5" />
                <span>Reviews</span>
              </div>
            </button>
          </div>
        </TabScroller>

        {/* About Tab Content */}
        {activeTab === 'about' && (
          <div className="space-y-6">
           {/* Media Gallery – trainer showcase */}
<TrainerMediaGallery
  trainerAuthUid={trainer.id}   // auth UID, not DB id
  isEditable={false}
/>


            {/* Bio Section - Moved below gallery */}
            <div className="bg-dark-200 rounded-2xl p-6 border border-dark-300">
              <h2 className="text-xl font-semibold text-white mb-4">
                {trainer.headline || `About ${trainer.name}`}
              </h2>
              <p className="text-dark-800 whitespace-pre-line">
                {(() => {
                  const full = trainer.bio?.trim() || 'No bio provided yet.';
                  if (full.length <= 300) return full;
                  return bioExpanded
                    ? full
                    : full.slice(0, 300).trimEnd() + '…';
                })()}
              </p>
              {trainer.bio?.trim().length > 300 && (
                <button
                  onClick={() => setBioExpanded(!bioExpanded)}
                  className="mt-2 text-sm font-medium text-gold-400 hover:underline"
                >
                  {bioExpanded ? 'Show less' : 'Read more'}
                </button>
              )}
            </div>

            
{trainer.company && (
  <div className="bg-dark-200 rounded-2xl p-6 border border-dark-300">
    <h2 className="text-xl font-semibold text-white mb-2 flex items-center">
      <Briefcase className="h-5 w-5 text-gold-400 mr-2" />
      Company
    </h2>
    <p className="text-dark-800">{trainer.company}</p>
  </div>
)}
            {/* Specialties */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Specialties */}
              <div className="bg-dark-200 rounded-2xl p-6 border border-dark-300 md:col-span-2">
                <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
                  <Dumbbell className="h-5 w-5 text-gold-400 mr-2" />
                  Available Session Types
                </h2>
                <div className="space-y-6">
                {sessions.map((session, idx) => {
                  const formatPrice = (cents: number) => (cents / 100).toLocaleString(undefined, { 
                    style: 'currency', 
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  });
                  
                  const isGroupSession = session.title.toLowerCase().includes('group') || (session.group_min && session.group_min > 1);
                  const sessionKey = `${idx}-${session.title}`;
                  const isExpanded = expandedSessions[sessionKey] || false;
                  
                  return (
                    <div
                      onClick={() => {
                        setSessionToBook(session);
                        setShowTrainerBookingModal(true);
                      }}
                      className={cn(
                        "relative overflow-hidden group",
                        isDarkMode 
                          ? "bg-gradient-to-br from-dark-200 to-dark-300/80" 
                          : "bg-gradient-to-br from-white to-gray-100",
                        "border border-dark-400/30 dark:border-dark-400/30 light:border-gray-200",
                        "hover:border-gold-400/50 dark:hover:border-gold-400/50 light:hover:border-gold-500/50",
                        "rounded-2xl shadow-md hover:shadow-xl",
                        "transition-all duration-300 transform hover:scale-[1.02]"
                      )}
                    >
                      {/* Decorative elements */}
                      <div className={cn(
                        "absolute -right-12 -top-12 w-40 h-40 rounded-full blur-xl transition-all duration-500",
                        isDarkMode 
                          ? "bg-gold-400/5 group-hover:bg-gold-400/10" 
                          : "bg-gold-400/10 group-hover:bg-gold-400/15"
                      )}></div>
                      <div className={cn(
                        "absolute -left-12 -bottom-12 w-40 h-40 rounded-full blur-xl transition-all duration-500",
                        isDarkMode 
                          ? "bg-gold-400/5 group-hover:bg-gold-400/10" 
                          : "bg-gold-400/10 group-hover:bg-gold-400/15"
                      )}></div>
                      
                      <div className="relative p-6 sm:p-8">
                        <div className="flex flex-col sm:flex-row gap-6">
                          {/* Left: Icon + Title + Description */}
                          <div className="flex gap-5">
                            <div className={cn(
                              "flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center",
                              isDarkMode
                                ? "bg-gradient-to-br from-gold-400/20 to-gold-400/10 group-hover:from-gold-400/30 group-hover:to-gold-400/20"
                                : "bg-gradient-to-br from-gold-500/15 to-gold-400/5 group-hover:from-gold-500/25 group-hover:to-gold-400/15",
                              "transition-all duration-300"
                            )}>
                              {isGroupSession ? (
                                <Users className="h-8 w-8 text-gold-400" />
                              ) : (
                                <Dumbbell className="h-8 w-8 text-gold-400" />
                              )}
                            </div>
                            
                            <div className="flex-1">
                              <div className="flex items-start gap-3 mb-2">
                                <div className={cn(
                                  "px-3 py-1 rounded-full text-xs font-medium",
                                  isDarkMode 
                                    ? "bg-gold-400/20 text-gold-400" 
                                    : "bg-gold-100 text-gold-700"
                                )}>
                                  {isGroupSession ? 'Group Session' : 'Private Session'}
                                </div>
                              </div>
                              
                              <h3 className={cn(
                                "text-2xl font-bold transition-colors duration-300",
                                isDarkMode ? "text-white" : "text-gray-900"
                              )}>
                                {session.title}
                              </h3>
                              
                              <div className="mt-2 max-w-lg">
                                {(() => {
                                  const full = session.description?.trim() || '';
                                  const isLong = full.length > 120;
                                  
                                  return (
                                    <>
                                      <p className={cn(
                                        isDarkMode ? "text-dark-800" : "text-gray-600"
                                      )}>
                                        {isLong && !isExpanded 
                                          ? full.slice(0, 120).trimEnd() + '…' 
                                          : full}
                                      </p>
                                      {isLong && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedSessions(prev => ({
                                              ...prev,
                                              [sessionKey]: !isExpanded
                                            }));
                                          }}
                                          className={cn(
                                            "mt-1 text-sm font-medium",
                                            "flex items-center",
                                            isDarkMode 
                                              ? "text-gold-400 hover:text-gold-300" 
                                              : "text-gold-600 hover:text-gold-700"
                                          )}
                                        >
                                          {isExpanded ? 'Show less' : 'Read more'}
                                        </button>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                              
                              {/* Session Details */}
                              <div className="flex flex-wrap gap-3 mt-4">
                                {/* Duration */}
                                <div className={cn(
                                  "flex items-center gap-1.5",
                                  isDarkMode ? "text-dark-800" : "text-gray-600"
                                )}>
                                  <Clock className="h-4 w-4 text-gold-400" />
                                  <span>{session.duration_min} minutes</span>
                                </div>
                                
                                {/* Group Size (if applicable) */}
                                {isGroupSession && (
                                  <div className={cn(
                                    "flex items-center gap-1.5",
                                    isDarkMode ? "text-dark-800" : "text-gray-600"
                                  )}>
                                    <Users className="h-4 w-4 text-gold-400" />
                                    <span>{session.group_min || 2}-{session.group_max || 4} people</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Right: Price + Book Button */}
                          <div className="flex flex-col items-center sm:items-end gap-4 mt-4 sm:mt-0 sm:ml-auto">
                            <div className="flex flex-col items-center sm:items-end">
                              <div className="text-3xl font-bold text-gold-400">
                                {formatPrice(session.price_cents)}
                              </div>
                              {session.price_mode === 'per_person' && (
                                <div className={cn(
                                  "text-sm",
                                  isDarkMode ? "text-dark-800" : "text-gray-600"
                                )}>
                                  per person
                                </div>
                              )}
                            </div>
                            
                            <Button
                              onClick={handleBookNow}
                              className={cn(
                                "min-w-[180px] px-6 py-4 rounded-xl",
                                isDarkMode
                                  ? "bg-gradient-to-r from-gold-400 to-gold-500 hover:from-gold-500 hover:to-gold-600"
                                  : "bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700",
                                "text-dark-50 font-medium",
                                "shadow-lg hover:shadow-xl",
                                "transform hover:scale-105 active:scale-95",
                                "transition-all duration-300"
                              )}
                            >
                              <Calendar className="h-5 w-5 mr-2" />
                              Book This Session
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                </div>
              </div>


            </div>

          </div>
        )}

        {/* Reviews Tab Content */}
        {activeTab === 'reviews' && (
          <div className="space-y-6">
            {/* Review Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-dark-200 rounded-2xl p-6 border border-dark-300">
                <div className="flex items-center justify-center flex-col">
                  <div className="text-5xl font-bold text-gold-400 mb-2">{trainer.rating}</div>
                  <div className="flex items-center mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={cn(
                          "h-5 w-5",
                          i < Math.floor(trainer.rating) ? "text-gold-400" : "text-dark-500"
                        )}
                        fill={i < Math.floor(trainer.rating) ? "currentColor" : "none"}
                      />
                    ))}
                  </div>
                  <p className="text-dark-800">{trainer.reviews} reviews</p>
                </div>
              </div>

              <div className="bg-dark-200 rounded-2xl p-6 border border-dark-300">
                <h3 className="text-white font-medium mb-4">Rating Breakdown</h3>
                <div className="space-y-3">
                {[
  { name: 'Punctuality',   value: trainer.avgPunctuality   },
  { name: 'Communication', value: trainer.avgCommunication },
  { name: 'Sportsmanship', value: trainer.avgSportsmanship },
  { name: 'Skill Match',   value: trainer.avgSkillMatch    }
].map((item) => (
  <div key={item.name} className="flex items-center">
    <span className="text-dark-800 w-32">{item.name}</span>
    <div className="flex-1 h-2 bg-dark-400 rounded-full overflow-hidden">
      <div
        className="h-full bg-gold-400"
        style={{ width: `${(item.value / 5) * 100}%` }}
      />
    </div>
    <span className="text-white ml-3 w-8">
      {item.value.toFixed ? item.value.toFixed(1) : item.value}
    </span>
  </div>
))}

                </div>
              </div>
            </div>

       {/* Real Reviews */}
<div className="space-y-4">
  {reviews.length === 0 && (
    <p className="text-dark-800">No reviews yet.</p>
  )}

  {reviews.map(r => (
    <div key={r.id} className="bg-dark-200 rounded-xl p-6 border border-dark-300">
      <div className="flex items-start">
        <img
          src={r.author.avatar_url || '/avatar-placeholder.png'}
          alt={r.author.full_name}
          className="w-12 h-12 rounded-full object-cover mr-4"
        />
        <div className="flex-1">
          <div className="flex justify-between items-start">
            <div>
              <h4 className="text-white font-medium">{r.author.full_name}</h4>
              <div className="flex items-center mt-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={cn(
                      "h-4 w-4",
                      i < Math.round(r.rating_overall) ? "text-gold-400" : "text-dark-500"
                    )}
                    fill={i < Math.round(r.rating_overall) ? "currentColor" : "none"}
                  />
                ))}
                <span className="text-white ml-2">
                  {parseFloat(r.rating_overall).toFixed(1)}
                </span>
              </div>
            </div>
            <span className="text-dark-800 text-sm">
              {new Date(r.timestamp).toLocaleDateString()}
            </span>
          </div>
          <p className="text-dark-800 mt-3">{r.feedback}</p>
        </div>
      </div>
    </div>
  ))}
</div>

          </div>
        )}

        {/* Sessions Tab Content */}
        {activeTab === 'sessions' && (
          <div className="space-y-6">
            {/* Session Types */}
            <div className="bg-dark-200 rounded-2xl p-6 border border-dark-300">
              <h2 className="text-xl font-semibold text-white mb-4">Available Session Types</h2>
              
              <div className="space-y-4">
               {sessions.map((session, idx) => {
  const priceLabel =
    session.price_mode === 'per_person'
      ? `${(session.price_cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}/person`
      : (session.price_cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });

  return (
    <div key={idx} className="bg-dark-300 rounded-xl p-4 hover:bg-dark-400 transition-colors">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-white font-medium">{session.title}</h3>
          <p className="text-dark-800 text-sm mt-1">{session.description}</p>
        </div>
        <div className="text-right">
          <div className="text-gold-400 font-bold">{priceLabel}</div>
          <div className="text-dark-800 text-sm">{session.duration_min} min</div>
        </div>
      </div>
      <div className="mt-4">
        <Button
          onClick={handleBookNow}
          className="bg-gold-400 hover:bg-gold-500 text-dark-50 w-full"
        >
          Book This Session
        </Button>
      </div>
    </div>
  );
})}

              </div>
            </div>
            
            {/* Booking CTA */}
            <div className="bg-dark-200 rounded-2xl p-6 border border-dark-300 text-center">
              <h2 className="text-xl font-semibold text-white mb-4">Ready to start your fitness journey?</h2>
              <p className="text-dark-800 mb-6">Book a session with {trainer.name} and take the first step towards your goals.</p>
              <div className="flex justify-center gap-4">
                <Button
                  onClick={handleBookNow}
                  className="bg-gold-400 hover:bg-gold-500 text-dark-50 px-8 py-3"
                >
                  <Calendar className="h-5 w-5 mr-2" />
                  Book Now
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Moments Tab Content */}
        {activeTab === 'moments' && (
          <PostsTab 
            posts={[]} 
            memberName={trainer.name} 
            memberId={id}
          />
        )}
        
        {/* Connections Tab Content */}
        {activeTab === 'connections' && (
 <NetworkTab memberId={trainer.id} /> 
        )}
        
        {/* Cities Played Modal */}
        {showCitiesModal && (
          <CitiesPlayedModal
            isOpen={true}
            cities={trainerCitiesList}
            memberName={trainer.name}
            onClose={() => setShowCitiesModal(false)}
          />
        )}
        
        {/* Matches Modal */}
        {showMatchesModal && (
          <MemberMatchesModal
            isOpen={true}
            onClose={() => setShowMatchesModal(false)}
            memberId={trainer.id}
            memberName={trainer.name}
          />
        )}
        
        {/* Trainer Booking Modal */}
        {trainer && (
          <TrainerBookingModal
            isOpen={showTrainerBookingModal}
            onClose={() => setShowTrainerBookingModal(false)}
            trainer={{
              id: trainer.id,
              name: trainer.name,
              city: trainer.city,
              image: trainer.image
            }}
            preSelectedSession={sessionToBook}
            sessions={sessions}
          />
        )}
        </div>
    </DashboardLayout>
  );
};

export default TrainerProfile;